<template>
  <div class="calendar">
    <h2>Dostępne wizyty</h2>

    <!-- Spinner while loading hours -->
    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
    </div>

    <div v-else>
      <div v-for="(slots, date) in groupedHours" :key="date" class="day-block">
        <button class="date-btn" @click="toggleDate(date)">
          {{ date }}
          <span>{{ expandedDates.includes(date) ? "▲" : "▼" }}</span>
        </button>

        <div>
          <div v-if="expandedDates.includes(date)" class="slots-vertical">
            <div
              v-for="slot in slots"
              :key="slot.time"
              class="slot-tile"
              @click="selectSlot(slot)"
            >
              <div class="slot-time">{{ slot.time }}</div>
              <div class="slot-workers">
                <div v-for="wid in slot.workerIds" :key="wid">
                  {{ workerNames[wid] || wid }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Doctor selection modal -->
    <div v-if="showDoctorModal && selectedSlot" class="modal-overlay">
      <div class="modal">
        <h3>Wybierz lekarza</h3>
        <div class="worker-buttons">
          <button
            v-for="workerId in selectedSlot.workerIds"
            :key="workerId"
            @click="chooseWorker(workerId)"
            class="slot-tile"
          >
            {{ workerNames[workerId] || workerId }}
          </button>
        </div>
        <button class="cancel-btn" @click="closeDoctorModal">Anuluj</button>
      </div>
    </div>

    <!-- Booking form modal -->
    <div v-if="showBookingModal && selectedSlot" class="modal-overlay">
      <div class="modal">
        <h3>Zaplanuj wizytę</h3>
        <p>
          {{ selectedSlot.date }} o {{ selectedSlot.time }} z 
          {{ workerNames[selectedSlot.chosenWorkerId] }}
          <button 
            v-if="selectedSlot.workerIds.length > 1"
            @click="changeDoctor" 
            class="cancel-btn" 
            style="margin-left: 10px; padding: 2px 5px; font-size: 0.8rem;"
          >
            Zmień lekarza
          </button>
        </p>
        <form @submit.prevent="bookAppointment">
          <input v-model="patient.name" placeholder="Imię" required />
          <input v-model="patient.surname" placeholder="Nazwisko" required />
          <input v-model="patient.phone" placeholder="Numer telefonu" required />
          <input v-model="patient.email" placeholder="Email" type="email" required />
          <input v-model="patient.dateOfBirth" placeholder="Data urodzenia" type="date" required />
          <button type="submit" class="book-btn">Zaplanuj wizytę</button>
          <button type="button" @click="closeBookingModal" class="cancel-btn">Anuluj</button>
        </form>
      </div>
    </div>

    <!-- Kids modal if under 16 -->
    <div v-if="showKidsModal" class="modal-overlay">
      <div class="modal">
        <h3>Informacja</h3>
        <p>Wizyty dla osób poniżej 16 roku życia należy umawiać telefonicznie.</p>
        <button class="cancel-btn" @click="closeKidsModal">Zamknij</button>
      </div>
    </div>

    <!-- Visit exists modal -->
    <div v-if="showVisitExistsModal" class="modal-overlay">
      <div class="modal">
        <h3>Informacja</h3>
        <p>Nie jest to Twoja pierwsza wizyta lub masz ją już zaplanowaną.</p>
        <button class="cancel-btn" @click="closeVisitExistsModal">Zamknij</button>
      </div>
    </div>

    <!-- Success booking modal -->
    <div v-if="showSuccessModal" class="modal-overlay">
      <div class="modal">
        <h3>Sukces!</h3>
        <p>✅ Wizyta została zaplanowana pomyślnie.</p>
        <button class="cancel-btn" @click="closeSuccessModal">
          Powrót
        </button>
      </div>
    </div>

    <!-- Error booking modal -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal">
        <h3>Błąd</h3>
        <p>❌ Nie udało się zaplanować wizyty. Spróbuj ponownie później.</p>
        <button class="cancel-btn" @click="closeErrorModal">Zamknij</button>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

const workerNames = {
  12531946: "Piotr Szymajda",
  12541391: "Michał Rochala",
  13075430: "Angela Jasińska",
  13092685: "Lawin Almalla",
  13092757: "Ewa Świech",
  13092836: "Wiesława Dziarska",
  14547278: "Bartłomiej Klęsk"
};

export default {
  name: "AppointmentsCalendar",
  data() {
    return {
      freeHours: [],
      loading: false,
      selectedSlot: null,
      expandedDates: [],
      patient: {
        name: "",
        surname: "",
        phone: "",
        email: "",
        dateOfBirth: ""
      },
      showDoctorModal: false,
      showBookingModal: false,
      showKidsModal: false,
      showVisitExistsModal: false,
      showSuccessModal: false,
      showErrorModal: false,
      workerNames
    };
  },
  computed: {
    groupedHours() {
      return this.freeHours.reduce((groups, slot) => {
        if (!groups[slot.date]) groups[slot.date] = [];

        const existingSlot = groups[slot.date].find(s => s.time === slot.time);

        if (existingSlot) {
          if (!existingSlot.workerIds.includes(slot.worker_id)) {
            existingSlot.workerIds.push(slot.worker_id);
          }
        } else {
          groups[slot.date].push({
            ...slot,
            workerIds: [slot.worker_id]
          });
        }

        return groups;
      }, {});
    }
  },
  methods: {
    async fetchFreeHours() {
      this.loading = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/api/freehours`);
        this.freeHours = res.data;
      } catch (err) {
        console.error("Error loading free hours:", err);
      } finally {
        this.loading = false;
      }
    },
    toggleDate(date) {
      if (this.expandedDates.includes(date)) {
        this.expandedDates = this.expandedDates.filter(d => d !== date);
      } else {
        this.expandedDates.push(date);
      }
    },
    selectSlot(slot) {
      if (!slot) return;
      this.selectedSlot = { 
        ...slot, 
        chosenWorkerId: slot.workerIds[0] || null 
      };
      this.showBookingModal = true;
    },
    changeDoctor() {
      if (!this.selectedSlot || this.selectedSlot.workerIds.length <= 1) return;
      this.showBookingModal = false;
      this.showDoctorModal = true;
    },
    chooseWorker(workerId) {
      if (!this.selectedSlot) return;
      this.selectedSlot.chosenWorkerId = workerId;
      this.showDoctorModal = false;
      this.showBookingModal = true;
    },
    closeDoctorModal() {
      this.showDoctorModal = false;
      this.showBookingModal = true;
    },
    closeBookingModal() {
      this.showBookingModal = false;
      this.selectedSlot = null;
      this.resetPatient();
    },
    closeKidsModal() {
      this.showKidsModal = false;
      this.selectedSlot = null;
      this.resetPatient();
    },
    openVisitExistsModal() {
      this.showVisitExistsModal = true;
    },
    closeVisitExistsModal() {
      this.showVisitExistsModal = false;
      this.selectedSlot = null;
      this.resetPatient();
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      window.location.reload();
    },
    closeErrorModal() {
      this.showErrorModal = false;
    },
    resetPatient() {
      this.patient = { name: "", surname: "", phone: "", email: "", dateOfBirth: "" };
    },
    async bookAppointment() {
      if (!this.selectedSlot) return;

      // Age validation
      const dob = new Date(this.patient.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      if (age < 16) {
        this.showBookingModal = false;
        this.showKidsModal = true;
        return;
      }

      this.loading = true;
      this.showBookingModal = false;
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/api/appointments/book`, {
          patientInfo: { ...this.patient },
          workerId: this.selectedSlot.chosenWorkerId,
          date: this.selectedSlot.date,
          slot: this.selectedSlot.time,
          desc: "Zaplanowano z kalendarza Vue"
        });

        if (res.data.message === "exists_visit") {
          
          this.openVisitExistsModal();
          return;
        }

        // ✅ Success modal
        this.closeBookingModal();
        this.showSuccessModal = true;
      } catch (err) {
        console.error("Error booking appointment:", err);
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchFreeHours();
  }
};
</script>

<style scoped>
.calendar {
  padding: 0% 30%;
}

/* Spinner styles */
.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
}
.spinner {
  border: 4px solid rgba(0,0,0,0.1);
  border-left-color: #007bff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Date button */
.date-btn {
  width: 100%;
  padding: 0.75rem;
  background: #007bff;
  color: white;
  font-weight: bold;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  margin-bottom: 0.5rem;
}
.date-btn:hover {
  background: #0056b3;
}

/* Vertical slots */
.slots-vertical {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

/* Slot tiles */
.slot-tile {
  padding: 0.75rem;
  border: 1px solid #007bff;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  background: white;
}
.slot-tile:hover {
  background: #007bff;
  color: white;
}
.slot-tile.selected {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.slot-time {
  font-size: 1rem;
  font-weight: bold;
}

.slot-workers {
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}
.modal h3 {
  margin-bottom: 1rem;
}

/* Form */
form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
input {
  padding: 0.5rem;
  border: 1px solid #007bff;
  border-radius: 6px;
}

.book-btn {
  background: #28a745;
  color: white;
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.cancel-btn {
  background: #dc3545;
  color: white;
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 0.5rem;
}

/* Worker buttons inside modal */
.worker-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}
</style>
